import { Editor, Delta } from '@typewriter/editor';
import { getDefaultPaper, Paper } from '@typewriter/view';
import { View } from '@typewriter/view-vdom';
import naughtywords from 'naughty-words/en.json';
import badwords from 'badwords-list/lib/array';
import {
  input,
  keyShortcuts,
  history,
  shortcuts,
  smartEntry,
  smartQuotes,
} from '@typewriter/modules';
import { countWords } from './libs/word-count';
import spaces from './editor-modules/spaces';
import links from './editor-modules/links';
import placeholder from './editor-modules/placeholder';
import { hoverMenu } from './editor-modules/hover-menu';


type EditorModule = (editor?: Editor, root?: Element, paper?: Paper) => {onDestroy: () => void};


export interface EditorWithView extends Editor {
  view: View;
}

export interface SprintEditor extends EditorWithView {
  sprintId: string;
}


const allBadWords = new Set();
naughtywords.concat(badwords).forEach(word => allBadWords.add(word));
const badWordsExp = new RegExp(`\\b(${Array.from(allBadWords).join('|')})\\b`, 'gi')
export const paper = getPaper();


export function createEditor(root: HTMLElement, contents = new Delta()): EditorWithView {
  const editor = new Editor({ contents }) as EditorWithView;
  const modules = getModules();
  editor.view = new View(editor, paper, { modules, root });
  return editor;
}


export function getEditor(): Editor {
  return new Editor();
}

export function getView(editor: Editor, root: HTMLElement): View {
  const modules = getModules();
  return new View(editor, paper, { modules, root });
}

export function getText(delta: Delta) {
  return delta
    .reduce((text, op) => text + (typeof op.insert === 'string' ? op.insert : ' '), '')
    .replace(/\n$/, '');
}

export function getWordcount(delta: Delta) {
  return countWords(getText(delta));
}

export function sanitizeText(text: string) {
  return text.replace(badWordsExp, (word) => word.replace(/\w/g, '*'));
}

export function sanitizeDelta(delta: Delta) {
  const clean = new Delta();
  const original = getText(delta);
  let pos = 0;
  original.replace(badWordsExp, (word, _, index) => {
    clean.retain(index - pos);
    clean.delete(word.length).insert(word.replace(/\w/g, '*'));
    pos = index + word.length;
    return word;
  });
  return clean.length() ? delta.compose(clean) : delta;
}

/* EDITOR/VIEW SETUP */

export function getPaper() {
  const paper = getDefaultPaper();
  paper.blocks.remove('code-block');
  paper.marks.remove('code');
  return paper;
}

function getModules() {
  const modules: Record<string, EditorModule> = {
    input: input(),
    shortcuts: shortcuts(null),
    keyShortcuts: keyShortcuts(),
    history: history({ delay: 4000 }),
    smartEntry: smartEntry(),
    smartQuotes: smartQuotes(),
    hoverMenu: hoverMenu(),
    spaces: spaces(),
    links: links(),
    placeholder: placeholder((editor, root) => root.getAttribute('placeholder')),
  };
  return modules;
}
